// Which camera photographs a copy in a browser: the rear one when the device
// has one (a phone, a tablet, a 2-in-1), the front one otherwise (a laptop's
// webcam). Read from the device list, which never prompts: before the camera
// permission is granted the list has no labels, and the browser is left to
// choose with a preference for the rear camera.

const REAR_LABEL = /\b(back|rear|environment)\b|arri[eè]re/i

const facesBack = (device) => {
    const capabilities =
        typeof device?.getCapabilities === "function"
            ? device.getCapabilities()
            : null
    if (capabilities?.facingMode?.includes?.("environment")) return true
    return REAR_LABEL.test(device?.label ?? "")
}

/**
 * "environment" when a video input faces back, or when the list says nothing
 * (no permission yet: a preference, the browser falls back to the front
 * camera); "user" when the cameras are known and none faces back.
 *
 * On a phone or a tablet (`handheld`), always "environment": the camera names
 * are translated into the device's language ("Rückkamera", "Cámara trasera"),
 * so a name that says nothing we read does not mean there is no rear camera.
 * It stays a preference (`ideal`): a device without one opens its front camera.
 */
export const preferredFacingMode = (devices, { handheld = false } = {}) => {
    if (handheld) return "environment"
    const cameras = (devices ?? []).filter(
        (device) => device?.kind === "videoinput"
    )
    if (cameras.some(facesBack)) return "environment"
    const known = cameras.some((device) => !!device?.label)
    return known ? "user" : "environment"
}

/**
 * A phone or a tablet: what expo-device reads from the browser, or a touch
 * screen (an iPad's Safari says it is a Mac).
 */
export const isHandheld = (deviceType, phone, tablet) =>
    deviceType === phone ||
    deviceType === tablet ||
    (globalThis.navigator?.maxTouchPoints ?? 0) > 1

/** The device list, or nothing where the browser has none to give. */
export const listMediaDevices = async () => {
    try {
        const devices =
            await globalThis.navigator?.mediaDevices?.enumerateDevices?.()
        return Array.isArray(devices) ? devices : []
    } catch (error) {
        console.warn("[camera] enumerateDevices failed", error)
        return []
    }
}
